import { GLContext } from 'nanogl/types'
import Node from 'nanogl-gltf/lib/elements/Node'

import Delay from '@/webgl/core/Delay'
import { RenderContext } from '@/webgl/core/RenderContext'
import Renderer from '@/webgl/Renderer'
import { TimeEntity } from '@/webgl/runner/entities/Time'
import GameWorld from '@/webgl/runner/GameWorld'
import { IRunnerRenderer } from '@/webgl/runner/interfaces/IRunnerRenderer'
import BackgroundRenderer from '@/webgl/runner/rendering/BackgroundRenderer'
import CollectibleRenderer from '@/webgl/runner/rendering/CollectibleRenderer'
import EyeMaskRenderer from '@/webgl/runner/rendering/EyeMaskRenderer'
import LanesRenderer from '@/webgl/runner/rendering/LanesRenderer'
import ParticlesRenderer from '@/webgl/runner/rendering/ParticlesRenderer'
import PlayerRenderer from '@/webgl/runner/rendering/PlayerRenderer'
import PlayerStatusRenderer, { StatusType } from '@/webgl/runner/rendering/PlayerStatusRenderer'
import RailsRenderer from '@/webgl/runner/rendering/RailsRenderer'
import SmokeRenderer from '@/webgl/runner/rendering/SmokeRenderer'
import SpotlightsRenderer from '@/webgl/runner/rendering/SpotlightsRenderer'
import { ScenarioSetup } from '@/webgl/runner/scenarios/ScenarioSetup.types'

export default class SceneRenderer implements IRunnerRenderer {
  node: Node
  gl: GLContext
  world: GameWorld
  glRenderer: Renderer
  eye: EyeMaskRenderer
  background: BackgroundRenderer
  lanes: LanesRenderer
  collectible: CollectibleRenderer
  particles: ParticlesRenderer
  smoke: SmokeRenderer
  player: PlayerRenderer
  status: PlayerStatusRenderer
  rails: RailsRenderer
  spotlights: SpotlightsRenderer

  _rescount: number = 0
  _resloaded: number = 0
  _loaded: boolean = false

  constructor(renderer: Renderer, world: GameWorld, setup: ScenarioSetup) {
    this.glRenderer = renderer
    this.world = world
    this.gl = renderer.gl

    this.lanes = new LanesRenderer(this.gl, this.world)
    this.eye = new EyeMaskRenderer(this.gl, this.world)
    this.background = new BackgroundRenderer(this.gl, this.world)
    this.smoke = new SmokeRenderer(this.gl, this.world)
    this.collectible = new CollectibleRenderer(this.gl, this.world)
    this.particles = new ParticlesRenderer(this.gl, this.world)
    this.player = new PlayerRenderer(this.gl, this.world)
    this.rails = new RailsRenderer(this.gl, this.world)
    this.status = new PlayerStatusRenderer(this.gl, this.world)
    this.spotlights = new SpotlightsRenderer(this.gl, this.world)

    this.background.setResources(setup.background.resources)
    this.collectible.setResources(setup.collectibles.resources)
    this.particles.setResources(setup.particles.resources)

    this.background.setColor(setup.backgroundColor)

    this.lanes.setColor(setup.lanesColor)
    this.lanes.scroll.setTintColor(setup.lanesExtraColor)
    this.lanes.setMaskColor(setup.backgroundColor)
    this.lanes.setTextureId(setup.lanesTextureId)

    this.smoke.setColor(setup.smokeColor)
    this.player.setColors(setup.playerColor, setup.playerGoodColor, setup.playerWrongColor)
    this.glRenderer.setClearColor(setup.clearColor)
  }

  useStatus(ids: StatusType[]) {
    this.status.use(ids)
  }

  useRails(flag: boolean): void {
    this.rails.setActive(flag)
  }

  useSmoke(flag: boolean): void {
    this.smoke.setActive(flag)
  }

  useSpotlights(flag: boolean): void {
    this.spotlights.setActive(flag)
  }

  async load(abort: AbortSignal): Promise<void> {
    this._loaded = false
    this._resloaded = 0
    this.world.state.setLoadProgress(0)

    const resources = [
      this.lanes.load(abort),
      this.eye.load(abort),
      this.background.load(abort),
      this.smoke.load(abort),
      this.collectible.load(abort),
      this.particles.load(abort),
      this.player.load(abort),
      this.rails.load(abort),
      this.spotlights.load(abort),
      this.status.load(abort),
    ]

    this._rescount = resources.length

    for (const resource of resources) {
      resource.then(this.onResourceLoaded)
    }

    // delay to make the 100% slightly visible in the ui
    resources.push(Delay(500))

    await Promise.all(resources)

    this._loaded = true
  }

  onResourceLoaded = () => {
    this._resloaded++
    const progress = this._resloaded / this._rescount
    this.world.state.setLoadProgress(progress)
  }

  preRender(time: TimeEntity) {
    // Guards the pre-load race only: the render loop can be (re)started by
    // pause/resume before assets finish loading, and renderers read load-only
    // state, so skip until ready. This does NOT cover teardown — _loaded is
    // never reset to false on dispose, so callers must stop the render loop
    // before disposing rather than relying on this guard.
    if (!this._loaded) return

    this.lanes.preRender(time)
    this.eye.preRender(time)
    this.background.preRender(time)
    this.smoke.preRender(time)
    this.collectible.preRender(time)
    this.particles.preRender(time)
    this.rails.preRender(time)
    this.player.preRender(time)
    this.status.preRender(time)
    this.spotlights.preRender(time)
  }

  rttPass(context: RenderContext): void {
    // Same pre-load guard as preRender; see that comment for the teardown caveat.
    if (!this._loaded) return

    this.collectible.rttPass(context)
  }

  render(context: RenderContext) {
    // Same pre-load guard as preRender; see that comment for the teardown caveat.
    if (!this._loaded) return

    this.eye.render(context)
    this.smoke.render(context)
    this.lanes.render(context)
    this.rails.render(context)
    this.player.render(context)
    this.background.render(context)
    this.collectible.render(context)
    this.particles.render(context)
    this.status.render(context)
    this.spotlights.render(context)
  }
}
